/**
 * Input-comparison signal: detects when two SUCCEEDED runs against the
 * SAME enrollment file (matched by inputMd5 + canvasEnv) produced
 * different ghost counts.
 *
 * Why this matters: ghost detection should be deterministic for a fixed
 * PeopleSoft input file (Canvas-side state aside). If we run the same
 * file twice and find MORE ghosts the second time, the detection logic
 * missed something on the first pass — actionable bug signal.
 *
 * Comparison rule (TIME-AWARE — a run is only ever compared against
 * EARLIER runs):
 *   Walk SUCCEEDED runs with a defined ghostsFound in queuedAt order,
 *   tracking the minimum count seen so far per (inputMd5, canvasEnv).
 *   A run is flagged as a mismatch when its ghostsFound is STRICTLY
 *   GREATER than that earlier minimum — i.e. a repeat pass found ghosts
 *   the first pass missed.
 *
 * Why earlier-only matters: the benign sequence "run finds N ghosts →
 * fix ships → re-run finds fewer" must NOT retroactively flag the
 * original run. Finding FEWER later is the expected outcome of fixing;
 * only finding MORE later is the actionable detection-bug signal.
 *
 * Not flagged:
 *   - Runs with no inputMd5 (pre-feature rows, or runs that failed
 *     before hashing finished).
 *   - The first run of a group (nothing earlier to compare against).
 *   - Runs whose ghostsFound is undefined.
 *   - Runs whose ghostsFound equals or is below every earlier count
 *     (less means a prior fix was applied; equal is expected when no
 *     fix has been submitted yet).
 */

import type { RunStatus } from "@canvas-sync/shared";

interface ComparableRun {
  runId: string;
  status: RunStatus;
  canvasEnv?: "prod" | "test";
  inputMd5?: string;
  ghostsFound?: number;
  /** ISO timestamp used to order runs; rows without one sort first. */
  queuedAt?: string;
}

/**
 * Returns a Set of runIds whose ghost count exceeds the minimum found in
 * EARLIER runs that processed the same (inputMd5, canvasEnv). The first
 * run of a group is never flagged — only a repeat pass that finds MORE
 * ghosts than a previous pass is the actionable signal.
 */
export function computeInputMismatches(runs: ComparableRun[]): Set<string> {
  // Chronological walk so each run is only compared against earlier ones —
  // a later run finding FEWER ghosts (fix applied in between) must not
  // retroactively flag the original.
  const sorted = [...runs].sort((a, b) =>
    (a.queuedAt ?? "").localeCompare(b.queuedAt ?? ""),
  );

  const minSoFar = new Map<string, number>();
  const mismatches = new Set<string>();
  for (const r of sorted) {
    if (r.status !== "SUCCEEDED") continue;
    if (!r.inputMd5) continue;
    if (r.ghostsFound === undefined) continue;
    const key = `${r.inputMd5}|${r.canvasEnv ?? "test"}`;
    const earlierMin = minSoFar.get(key);
    if (earlierMin !== undefined && r.ghostsFound > earlierMin) {
      mismatches.add(r.runId);
    }
    if (earlierMin === undefined || r.ghostsFound < earlierMin) {
      minSoFar.set(key, r.ghostsFound);
    }
  }
  return mismatches;
}

/**
 * Total count of mismatch rows in a list — used by the Dashboard's
 * "Inconsistent ghost counts" stat card.
 */
export function countInputMismatches(runs: ComparableRun[]): number {
  return computeInputMismatches(runs).size;
}

/**
 * Concise summary of one run's ghost count vs a comparison group. Used by
 * the RunDetail panel's related-runs card. Callers must pass only runs
 * that QUEUED EARLIER than `thisRun` as `others` — comparing against later
 * runs would re-introduce the retroactive-mismatch bug (an original run
 * flagged because a post-fix re-run found fewer ghosts).
 */
export type RelatedRunComparison =
  | { kind: "missing-data" }
  | { kind: "same"; min: number }
  | { kind: "lower"; min: number; thisCount: number }
  | { kind: "higher"; min: number; thisCount: number };

export function compareToGroup(
  thisRun: ComparableRun,
  others: ComparableRun[],
): RelatedRunComparison {
  if (thisRun.ghostsFound === undefined) return { kind: "missing-data" };
  const counts = others
    .filter((r) => r.status === "SUCCEEDED" && r.ghostsFound !== undefined)
    .map((r) => r.ghostsFound as number);
  if (counts.length === 0) return { kind: "same", min: thisRun.ghostsFound };
  const min = Math.min(...counts);
  if (thisRun.ghostsFound > min) {
    return { kind: "higher", min, thisCount: thisRun.ghostsFound };
  }
  if (thisRun.ghostsFound < min) {
    return { kind: "lower", min, thisCount: thisRun.ghostsFound };
  }
  return { kind: "same", min };
}

/**
 * Per-run novelty: is this the first time we've seen this enrollment file
 * in this canvasEnv, or has it been processed before?
 *
 * Walks the runs in chronological order so each run only counts earlier
 * runs (not later ones) as "priors". A run with no inputMd5 (pre-feature
 * row or pre-hashing failure) gets { isNew: false, priorCount: 0 } —
 * the UI can render those as "—".
 */
export interface InputNovelty {
  /** True when this is the first run we've seen with this md5 in this env. */
  isNew: boolean;
  /** Number of EARLIER runs that share the same (md5, env). 0 for new. */
  priorCount: number;
  /** False when the run has no inputMd5 — UI should render a hyphen. */
  known: boolean;
}

export function computeInputNovelty(
  runs: ComparableRun[],
): Map<string, InputNovelty> {
  const sorted = [...runs].sort((a, b) =>
    (a.queuedAt ?? "").localeCompare(b.queuedAt ?? ""),
  );

  const seenByKey = new Map<string, number>();
  const result = new Map<string, InputNovelty>();

  for (const r of sorted) {
    if (!r.inputMd5) {
      result.set(r.runId, { isNew: false, priorCount: 0, known: false });
      continue;
    }
    const key = `${r.inputMd5}|${r.canvasEnv ?? "test"}`;
    const priorCount = seenByKey.get(key) ?? 0;
    result.set(r.runId, {
      isNew: priorCount === 0,
      priorCount,
      known: true,
    });
    seenByKey.set(key, priorCount + 1);
  }

  return result;
}

export type { ComparableRun };
